/**
 * The browser's own ear and mouth.
 *
 * The default, and for this app probably the right one for most families: it
 * costs nothing, needs no account, works in Hebrew, and on most machines never
 * sends the audio anywhere. It is also the only option that shows words as they
 * are being spoken, which matters more than it sounds — somebody unsure whether
 * the microphone is even on can see that it is.
 *
 * Two browser facts worth knowing. Speech recognition is Chrome and Safari only,
 * hence `available()` and hence the other adapters. And `getVoices()` is empty
 * on first call in Chrome until the list loads, so it is waited for rather than
 * trusted.
 */
import type { Lang } from '../../domain/types';
import { bcp47, type ListenHandlers, type ListenOptions, type Listening, type SpeechToText, type TextToSpeech } from './types';

// Not in lib.dom in every TypeScript version, and prefixed in Safari.
interface RecognitionEvent {
  resultIndex: number;
  results: ArrayLike<{ 0: { transcript: string }; isFinal: boolean }>;
}
interface Recognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: RecognitionEvent) => void) | null;
  onerror: ((e: { error?: string }) => void) | null;
  onend: (() => void) | null;
}
type RecognitionCtor = new () => Recognition;

function recognitionCtor(): RecognitionCtor | null {
  const w = window as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/**
 * The recogniser's results, as one sentence.
 *
 * Desktop Chrome and Safari hand over each stretch of speech once, so the pieces
 * simply join. **Chrome on Android does not**: in continuous mode every new
 * result repeats everything said before it — "where is", "where is my",
 * "where is my father" — so joining them made a phone show and send every
 * question two and three times over. A piece that starts with everything heard
 * so far therefore replaces it, and a piece already at the end is dropped.
 * Compared without case or punctuation, because the repeats do not always agree
 * on either.
 */
export function joinResults(parts: string[]): string {
  const bare = (s: string) => s.toLowerCase().replace(/[^\p{L}\p{N}\s]/gu, '').replace(/\s+/g, ' ').trim();
  let text = '';
  for (const raw of parts) {
    const part = raw.replace(/\s+/g, ' ').trim();
    if (!part) continue;
    const had = bare(text);
    const now = bare(part);
    // Whole words only: "I" followed by "it's broken" is two pieces, not a repeat.
    if (now === had) {
      // The same words again: keep whichever copy has the capitals and punctuation.
      if (part.length > text.length) text = part;
    } else if (!had || now.startsWith(`${had} `)) text = part;
    else if (had.endsWith(` ${now}`)) continue;
    else text = `${text} ${part}`;
  }
  return text;
}

export function createBrowserStt(): SpeechToText {
  return {
    id: 'browser',
    live: true,
    available: () => !!recognitionCtor(),
    async start(lang: Lang, handlers: ListenHandlers, opts: ListenOptions = {}): Promise<Listening> {
      const Ctor = recognitionCtor();
      if (!Ctor) {
        handlers.onError('assistant.voice.failed');
        return { stop() {}, cancel() {} };
      }
      const rec = new Ctor();
      rec.lang = bcp47(lang);
      // The person presses the button again when they have finished; a pause
      // for thought is not the end of a question. See ListenOptions.
      rec.continuous = opts.continuous ?? false;
      rec.interimResults = true;
      rec.maxAlternatives = 1;

      let heard = '';
      let done = false;
      let cancelled = false;

      /*
       * Rebuild the whole sentence every time, rather than appending.
       *
       * `results` is cumulative: each event carries every result so far, and
       * `resultIndex` only says where the *changes* start. Appending from that
       * index looks right and is not — a result that is revised after it went
       * final gets added a second and a third time, and the person watches their
       * own question stutter across the screen three times over. Reading the
       * whole list and replacing is idempotent, so it does not matter how many
       * times the engine fires or what it revises.
       */
      rec.onresult = (e) => {
        const parts: string[] = [];
        for (let i = 0; i < e.results.length; i++) parts.push(e.results[i][0].transcript);
        heard = joinResults(parts);
        handlers.onPartial?.(heard);
      };
      rec.onerror = (e) => {
        if (done || cancelled) return;
        done = true;
        handlers.onError(e.error === 'not-allowed' || e.error === 'service-not-allowed' ? 'assistant.voice.denied' : e.error === 'audio-capture' ? 'assistant.voice.noMic' : 'assistant.voice.failed');
      };
      rec.onend = () => {
        if (done || cancelled) return;
        done = true;
        // Whatever is on screen is what they said — including a sentence the
        // engine never marked final, which is common when `stop()` cuts it off
        // mid-word. Ending with nothing is not an error either: they pressed the
        // microphone and thought better of it, and "that failed" would be a lie.
        handlers.onFinal(heard);
      };

      try {
        rec.start();
      } catch {
        handlers.onError('assistant.voice.failed');
        return { stop() {}, cancel() {} };
      }
      return {
        stop() {
          rec.stop();
        },
        cancel() {
          cancelled = true;
          rec.abort();
        },
      };
    },
  };
}

/** The voice list arrives late in Chrome; wait for it once rather than returning nothing. */
async function allVoices(): Promise<SpeechSynthesisVoice[]> {
  const now = window.speechSynthesis.getVoices();
  if (now.length) return now;
  return new Promise((resolve) => {
    const done = () => resolve(window.speechSynthesis.getVoices());
    window.speechSynthesis.addEventListener('voiceschanged', done, { once: true });
    setTimeout(done, 800);
  });
}

export function createBrowserTts(): TextToSpeech {
  return {
    id: 'browser',
    available: () => typeof window !== 'undefined' && 'speechSynthesis' in window,
    async voices() {
      if (!('speechSynthesis' in window)) return [];
      return (await allVoices()).map((v) => ({ id: v.name, name: `${v.name} (${v.lang})` }));
    },
    async speak(text: string, lang: Lang, voiceName?: string) {
      if (!('speechSynthesis' in window) || !text.trim()) return;
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.lang = bcp47(lang);
      if (voiceName) {
        const match = (await allVoices()).find((v) => v.name === voiceName);
        if (match) u.voice = match;
      }
      // A shade slower than default: this is being read to somebody who asked
      // for help, not a podcast.
      u.rate = 0.95;
      await new Promise<void>((resolve) => {
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          clearTimeout(guard);
          resolve();
        };
        /*
         * Web Speech does not always tell you it has finished. An utterance that
         * is cancelled, or started before the voice list has loaded, can fire
         * neither `end` nor `error` — and then the "Stop reading" button is stuck
         * on for the rest of the session. So there is a ceiling: a generous
         * estimate from the length of the text, after which we call it spoken.
         */
        const guard = setTimeout(finish, 3000 + text.length * 90);
        u.onend = finish;
        u.onerror = finish;
        try {
          window.speechSynthesis.speak(u);
        } catch {
          // Some engines throw rather than firing `error` — a voice that will
          // not speak must never take the answer down with it.
          finish();
        }
      });
    },
    stop() {
      if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    },
  };
}
